import { useState, useEffect, useCallback } from 'react';
import {
  Save,
  Building2,
  User,
  GraduationCap,
  Shield,
  Calculator,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { financeApi, storeApi } from '../lib/api';
import { Select } from '@/components/ui/Select';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Types use backend-compatible values
type BusinessType = 'jdg' | 'spzoo';
type TaxForm = 'linear' | 'scale' | 'lump_sum';
type LumpSumRate = number;
type HealthTier = 'auto' | 'tier1' | 'tier2' | 'tier3';
type ZusType = 'full' | 'preferential' | 'maly_zus_plus';
type TaxpayerType = 'small' | 'standard';
type CitForm = 'standard' | 'estonian';

interface TaxConfig {
  businessType: BusinessType;
  // JDG fields
  taxForm: TaxForm;
  lumpSumRate: LumpSumRate;
  lumpHealthTier: HealthTier;
  hasEmployment: boolean;
  isStudent: boolean;
  firstBusiness: boolean;
  zusType: ZusType;
  // Sp. z o.o. fields
  taxpayerType: TaxpayerType;
  citForm: CitForm;
  // VAT
  vatEnabled: boolean;
  vatRate: number;
  // Automatyczny dokument kosztu reklam Meta (per sklep)
  metaCostBasis: 'GROSS' | 'NET';
  metaCostVatRate: number;
}

interface ZusPreview {
  skladkaSpoleczna: number;
  skladkaZdrowotna: number;
  razem: number;
  breakdown: {
    emerytalna: number;
    rentowa: number;
    chorobowa: number;
    wypadkowa: number;
    funduszPracy: number;
  };
}

const DEFAULT_CONFIG: TaxConfig = {
  businessType: 'jdg',
  taxForm: 'linear',
  lumpSumRate: 8.5,
  lumpHealthTier: 'auto',
  hasEmployment: false,
  isStudent: false,
  firstBusiness: false,
  zusType: 'full',
  taxpayerType: 'standard',
  citForm: 'standard',
  vatEnabled: false,
  vatRate: 23,
  metaCostBasis: 'GROSS',
  metaCostVatRate: 23,
};

const DEFAULT_ZUS: ZusPreview = {
  skladkaSpoleczna: 0,
  skladkaZdrowotna: 0,
  razem: 0,
  breakdown: {
    emerytalna: 0,
    rentowa: 0,
    chorobowa: 0,
    wypadkowa: 0,
    funduszPracy: 0,
  },
};

const RYCZALT_RATES: { value: number; label: string }[] = [
  { value: 3, label: '3%' },
  { value: 5.5, label: '5,5%' },
  { value: 8.5, label: '8,5%' },
  { value: 12, label: '12%' },
  { value: 15, label: '15%' },
  { value: 17, label: '17%' },
];

const HEALTH_TIERS: { value: HealthTier; label: string }[] = [
  { value: 'auto', label: 'Automatycznie' },
  { value: 'tier1', label: 'Próg 1 — przychody ≤60 000 zł' },
  { value: 'tier2', label: 'Próg 2 — przychody ≤300 000 zł' },
  { value: 'tier3', label: 'Próg 3 — przychody >300 000 zł' },
];

const VAT_RATES: { value: number; label: string }[] = [
  { value: 23, label: '23%' },
  { value: 8, label: '8%' },
  { value: 5, label: '5%' },
  { value: 0, label: '0%' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(n: number): string {
  return n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface StoreOption {
  id: string;
  name: string;
  platform?: string;
}

export function FinanceTaxesPage() {
  const [config, setConfig] = useState<TaxConfig>(DEFAULT_CONFIG);
  const [globalConfig, setGlobalConfig] = useState<TaxConfig>(DEFAULT_CONFIG);
  const [zusPreview, setZusPreview] = useState<ZusPreview>(DEFAULT_ZUS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [zusLoading, setZusLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Store management
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('global');
  const [storeConfigs, setStoreConfigs] = useState<Record<string, Record<string, unknown>>>({});

  // Load config + stores on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [configRes, storesRes, storeConfigsRes] = await Promise.all([
          financeApi.getConfig(),
          storeApi.getAll(),
          financeApi.getStoreConfigs(),
        ]);
        const data = configRes.data as TaxConfig | null;
        if (data) {
          setConfig((prev) => ({ ...prev, ...data }));
          setGlobalConfig((prev) => ({ ...prev, ...data }));
        }
        const storeList = ((storesRes.data ?? storesRes) as StoreOption[]) || [];
        setStores(storeList);
        const sConfigs = (storeConfigsRes.data ?? storeConfigsRes) as Record<string, Record<string, unknown>>;
        setStoreConfigs(sConfigs || {});
      } catch {
        console.error('[FinanceTaxesPage] Failed to load config');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Switch config when store selection changes
  const switchToStore = (storeId: string) => {
    setSelectedStoreId(storeId);
    setSuccess('');
    setError('');
    if (storeId === 'global') {
      setConfig({ ...globalConfig });
    } else {
      const overrides = storeConfigs[storeId] || {};
      setConfig({ ...globalConfig, ...overrides } as TaxConfig);
    }
  };

  // Fetch ZUS preview whenever config changes — pass current unsaved config
  // so the preview updates live without requiring a save first
  const fetchZusPreview = useCallback(async () => {
    if (config.businessType !== 'jdg') return;
    setZusLoading(true);
    try {
      const { data } = await financeApi.getZusPreview({
        zusType: config.zusType,
        taxForm: config.taxForm,
        hasEtat: String(config.hasEmployment),
        isStudent: String(config.isStudent),
        ulgaNaStart: String(config.firstBusiness),
        healthTier: config.lumpHealthTier,
        ryczaltRate: String(config.lumpSumRate),
      });
      if (data) {
        setZusPreview({
          skladkaSpoleczna: data.skladkaSpoleczna ?? 0,
          skladkaZdrowotna: data.skladkaZdrowotna ?? 0,
          razem: data.razem ?? 0,
          breakdown: {
            emerytalna: data.breakdown?.emerytalna ?? 0,
            rentowa: data.breakdown?.rentowa ?? 0,
            chorobowa: data.breakdown?.chorobowa ?? 0,
            wypadkowa: data.breakdown?.wypadkowa ?? 0,
            funduszPracy: data.breakdown?.funduszPracy ?? 0,
          },
        });
      }
    } catch {
      // Silently fail — preview is non-critical
    } finally {
      setZusLoading(false);
    }
  }, [config]);

  useEffect(() => {
    if (!loading) {
      fetchZusPreview();
    }
  }, [loading, fetchZusPreview]);

  // Derived state
  const isJdg = config.businessType === 'jdg';
  const isSpZoo = config.businessType === 'spzoo';
  const isRyczalt = config.taxForm === 'lump_sum';
  const hideZusType = config.hasEmployment || config.isStudent || config.firstBusiness;

  // Updaters
  const update = <K extends keyof TaxConfig>(key: K, value: TaxConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setSuccess('');
  };

  // Save — send only fields relevant to the backend schema
  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        businessType: config.businessType,
        taxForm: config.taxForm,
        lumpSumRate: config.lumpSumRate,
        lumpHealthTier: config.lumpHealthTier,
        zusType: config.zusType,
        hasEmployment: config.hasEmployment,
        isStudent: config.isStudent,
        firstBusiness: config.firstBusiness,
        taxpayerType: config.taxpayerType,
        citForm: config.citForm,
        vatEnabled: config.vatEnabled,
        vatRate: config.vatRate,
      };
      if (selectedStoreId !== 'global') {
        // Podstawa kosztu Meta jest konfigurowana tylko per sklep
        payload.metaCostBasis = config.metaCostBasis;
        payload.metaCostVatRate = config.metaCostVatRate;
      }
      if (selectedStoreId === 'global') {
        await financeApi.updateConfig(payload);
        setGlobalConfig({ ...config });
        setSuccess('Konfiguracja domyślna została zapisana.');
        setSaveStatus('success');
        setTimeout(() => setSaveStatus('idle'), 3000);
      } else {
        await financeApi.updateStoreConfig(selectedStoreId, payload);
        setStoreConfigs((prev) => ({ ...prev, [selectedStoreId]: payload }));
        setSuccess(`Konfiguracja sklepu "${stores.find(s => s.id === selectedStoreId)?.name}" została zapisana.`);
        setSaveStatus('success');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    } catch {
      setError('Nie udało się zapisać konfiguracji.');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-text-primary">Konfiguracja podatkowa</h1>
        <p className="text-text-secondary mt-2">
          Ustaw formę działalności, opodatkowanie, ZUS i VAT
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center p-4 bg-success/10 border border-success/30 rounded-xl text-success">
          <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      {/* Store selector badges */}
      {stores.length > 0 && (
        <div className="space-y-2">
          <label className="text-xs text-text-secondary font-medium uppercase tracking-wider">Konfiguracja dla</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => switchToStore('global')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                selectedStoreId === 'global'
                  ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
                  : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
              }`}
            >
              Domyślna (wszystkie sklepy)
            </button>
            {stores.map((s) => {
              const hasOverride = !!storeConfigs[s.id];
              return (
                <button
                  key={s.id}
                  onClick={() => switchToStore(s.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    selectedStoreId === s.id
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                      : hasOverride
                        ? 'bg-surface-hover/50 text-purple-300/70 border border-purple-500/10 hover:text-purple-300 hover:border-purple-500/20'
                        : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
                  }`}
                >
                  {s.name}
                  {hasOverride && <span className="ml-1 text-purple-400">*</span>}
                </button>
              );
            })}
          </div>
          {selectedStoreId !== 'global' && (
            <p className="text-xs text-text-muted">
              Zmiany będą dotyczyć tylko sklepu "{stores.find(s => s.id === selectedStoreId)?.name}".
              Pola niezmienione dziedziczą z konfiguracji domyślnej.
            </p>
          )}
        </div>
      )}

      {/* Main layout: form + ZUS preview sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column — form */}
        <div className="lg:col-span-2 space-y-6">
          {/* ============================================================= */}
          {/* 1. Forma działalności                                            */}
          {/* ============================================================= */}
          <div className="card">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-accent-primary" />
              Forma działalności
            </h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <RadioCard
                selected={isJdg}
                onClick={() => update('businessType', 'jdg')}
                label="JDG (Jednoosobowa działalność)"
              />
              <RadioCard
                selected={isSpZoo}
                onClick={() => update('businessType', 'spzoo')}
                label="Sp. z o.o."
              />
            </div>
          </div>

          {/* ============================================================= */}
          {/* 2. JDG-specific options                                       */}
          {/* ============================================================= */}
          {isJdg && (
            <>
              {/* Tax form */}
              <div className="card">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-accent-primary" />
                  Forma opodatkowania
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <RadioCard
                    selected={config.taxForm === 'linear'}
                    onClick={() => update('taxForm', 'linear')}
                    label="Podatek liniowy (19%)"
                  />
                  <RadioCard
                    selected={config.taxForm === 'scale'}
                    onClick={() => update('taxForm', 'scale')}
                    label="Skala podatkowa (12%/32%)"
                  />
                  <RadioCard
                    selected={config.taxForm === 'lump_sum'}
                    onClick={() => update('taxForm', 'lump_sum')}
                    label="Ryczałt"
                  />
                </div>

                {/* Ryczalt sub-options */}
                {isRyczalt && (
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Stawka ryczaltu */}
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Stawka ryczałtu
                      </label>
                      <select
                        value={config.lumpSumRate}
                        onChange={(e) => update('lumpSumRate', parseFloat(e.target.value))}
                        className="input"
                      >
                        {RYCZALT_RATES.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Prog skladki zdrowotnej */}
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Próg składki zdrowotnej
                      </label>
                      <select
                        value={config.lumpHealthTier}
                        onChange={(e) => update('lumpHealthTier', e.target.value as HealthTier)}
                        className="input"
                      >
                        {HEALTH_TIERS.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Checkboxes — exemptions */}
              <div className="card">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-accent-primary" />
                  Zwolnienia i ulgi
                </h3>
                <div className="space-y-3">
                  <CheckboxRow
                    checked={config.hasEmployment}
                    onChange={(v) => update('hasEmployment', v)}
                    icon={<User className="w-4 h-4" />}
                    label="Mam etat (nie płacę ZUS społecznego)"
                  />
                  <CheckboxRow
                    checked={config.isStudent}
                    onChange={(v) => update('isStudent', v)}
                    icon={<GraduationCap className="w-4 h-4" />}
                    label="Jestem studentem <26 lat"
                  />
                  <CheckboxRow
                    checked={config.firstBusiness}
                    onChange={(v) => update('firstBusiness', v)}
                    icon={<Shield className="w-4 h-4" />}
                    label="Ulga na start (6 miesięcy bez ZUS społecznego)"
                  />
                </div>
              </div>

              {/* Rodzaj ZUS — hidden when any exemption checkbox is checked */}
              {!hideZusType && (
                <div className="card">
                  <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-accent-primary" />
                    Rodzaj ZUS
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <RadioCard
                      selected={config.zusType === 'full'}
                      onClick={() => update('zusType', 'full')}
                      label="Pełny ZUS"
                    />
                    <RadioCard
                      selected={config.zusType === 'preferential'}
                      onClick={() => update('zusType', 'preferential')}
                      label="ZUS preferencyjny (24 mies.)"
                    />
                    <RadioCard
                      selected={config.zusType === 'maly_zus_plus'}
                      onClick={() => update('zusType', 'maly_zus_plus')}
                      label="Mały ZUS Plus"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* ============================================================= */}
          {/* 3. Sp. z o.o. options                                         */}
          {/* ============================================================= */}
          {isSpZoo && (
            <>
              <div className="card">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-accent-primary" />
                  Status podatnika
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <RadioCard
                    selected={config.taxpayerType === 'small'}
                    onClick={() => update('taxpayerType', 'small')}
                    label="Mały podatnik (9% CIT)"
                  />
                  <RadioCard
                    selected={config.taxpayerType === 'standard'}
                    onClick={() => update('taxpayerType', 'standard')}
                    label="Standardowy (19% CIT)"
                  />
                </div>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-accent-primary" />
                  Forma CIT
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <RadioCard
                    selected={config.citForm === 'standard'}
                    onClick={() => update('citForm', 'standard')}
                    label="Standardowy CIT"
                  />
                  <RadioCard
                    selected={config.citForm === 'estonian'}
                    onClick={() => update('citForm', 'estonian')}
                    label="Estoński CIT"
                  />
                </div>
              </div>
            </>
          )}

          {/* ============================================================= */}
          {/* 4. VAT                                                        */}
          {/* ============================================================= */}
          <div className="card">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-accent-primary" />
              VAT
            </h3>

            <div className="space-y-4">
              <CheckboxRow
                checked={config.vatEnabled}
                onChange={(v) => update('vatEnabled', v)}
                icon={<Shield className="w-4 h-4" />}
                label="Jestem czynnym podatnikiem VAT"
              />

              {config.vatEnabled && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Stawka VAT
                  </label>
                  <select
                    value={config.vatRate}
                    onChange={(e) => update('vatRate', parseInt(e.target.value))}
                    className="input w-40"
                  >
                    {VAT_RATES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* ============================================================= */}
        {/* 5. ZUS Preview panel (right sidebar)                           */}
        {/* ============================================================= */}
        {isJdg && (
          <div className="lg:col-span-1">
            <div className="card sticky top-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-accent-primary" />
                Podgląd ZUS
                {zusLoading && <Loader2 className="w-4 h-4 animate-spin text-accent-primary" />}
              </h3>

              {/* Summary */}
              <div className="space-y-3 mb-6">
                <SummaryRow label="Składka społeczna" value={zusPreview.skladkaSpoleczna} />
                <SummaryRow label="Składka zdrowotna" value={zusPreview.skladkaZdrowotna} />
                <div className="pt-3 border-t border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-text-primary font-semibold">RAZEM</span>
                    <span className="text-accent-primary font-bold text-lg">
                      {fmt(zusPreview.razem)} zł/mies.
                    </span>
                  </div>
                </div>
              </div>

              {/* Breakdown */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
                  Rozkład składek
                </p>
                <BreakdownRow label="Emerytalna" value={zusPreview.breakdown.emerytalna} />
                <BreakdownRow label="Rentowa" value={zusPreview.breakdown.rentowa} />
                <BreakdownRow label="Chorobowa" value={zusPreview.breakdown.chorobowa} />
                <BreakdownRow label="Wypadkowa" value={zusPreview.breakdown.wypadkowa} />
                <BreakdownRow label="Fundusz Pracy" value={zusPreview.breakdown.funduszPracy} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Automatyczny koszt reklam Meta - tylko per sklep */}
      {selectedStoreId === 'global' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-text-primary mb-2">Automatyczny koszt reklam Meta</h3>
          <p className="text-xs text-text-muted">
            To ustawienie jest konfigurowane osobno dla każdego sklepu. Wybierz sklep powyżej
            (np. Lemon Glow), aby ustawić, czy kwota raportowana przez Meta jest traktowana
            jako brutto (system wyliczy netto) czy jako netto.
          </p>
        </div>
      )}
      {selectedStoreId !== 'global' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-text-primary mb-2">Automatyczny koszt reklam Meta</h3>
          <p className="text-xs text-text-muted mb-4">
            System raz dziennie zapisuje odczyt wydatków Meta, a po zamknięciu miesiąca tworzy dokument kosztu okresu.
            Wybierz, jak traktować kwotę raportowaną przez Meta dla tego sklepu.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
            <div>
              <label className="text-xs text-text-muted mb-1 block" htmlFor="metaCostBasis">Podstawa kwoty Meta</label>
              <Select
                id="metaCostBasis"
                value={config.metaCostBasis}
                onChange={(e) => update('metaCostBasis', e.target.value as 'GROSS' | 'NET')}
              >
                <option value="GROSS">Brutto (system wyliczy netto)</option>
                <option value="NET">Netto (kwota Meta jest już netto)</option>
              </Select>
            </div>
            {config.metaCostBasis === 'GROSS' && (
              <div>
                <label className="text-xs text-text-muted mb-1 block" htmlFor="metaCostVatRate">Stawka VAT do wyliczenia netto (%)</label>
                <input
                  id="metaCostVatRate"
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  className="input"
                  value={config.metaCostVatRate}
                  onChange={(e) => update('metaCostVatRate', Number(e.target.value) || 0)}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky save bar */}
      <div className="sticky bottom-0 z-30 -mx-1 px-1 pt-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-raised/95 backdrop-blur-sm border border-border shadow-lg">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary text-sm flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Zapisz
          </button>
          {saveStatus === 'success' && (
            <span className="flex items-center gap-1.5 text-xs text-success animate-fade-in">
              <CheckCircle className="w-3.5 h-3.5" /> Zapisano
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-xs text-danger animate-fade-in">
              <AlertCircle className="w-3.5 h-3.5" /> Błąd zapisu
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RadioCard({
  selected,
  onClick,
  label,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 border ${
        selected
          ? 'bg-accent-primary/10 border-accent-primary/50 text-accent-primary shadow-accent-primary/20'
          : 'bg-surface-raised/50 border-border-subtle text-text-secondary hover:border-border-strong hover:text-text-primary'
      }`}
    >
      {label}
    </button>
  );
}

function CheckboxRow({
  checked,
  onChange,
  icon,
  label,
}: {
  checked: boolean;
  onChange: (val: boolean) => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <label className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl cursor-pointer hover:bg-surface-hover/50 transition-colors">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
      />
      <span className="text-accent-primary">{icon}</span>
      <span className="text-text-primary text-sm">{label}</span>
    </label>
  );
}

function SummaryRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-text-secondary text-sm">{label}</span>
      <span className="text-text-primary font-medium">{fmt(value)} zł/mies.</span>
    </div>
  );
}

function BreakdownRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-text-muted">{label}</span>
      <span className="text-text-secondary">{fmt(value)} zł</span>
    </div>
  );
}

export default FinanceTaxesPage;
